import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';


export default function InstructorUploadPage() {
  const [activeTab, setActiveTab] = useState('upload');
  const [dragActive, setDragActive] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([
    {
      id: '1',
      name: 'Introduction to React Hooks.mp4',
      type: 'video',
      size: '245 MB',
      uploadDate: '2024-01-15',
      status: 'completed',
      courseId: '1',
      courseName: 'Complete React Development'
    },
    {
      id: '2',
      name: 'Course Syllabus.pdf',
      type: 'document',
      size: '2.1 MB',
      uploadDate: '2024-01-14',
      status: 'completed',
      courseId: '1',
      courseName: 'Complete React Development'
    },
    {
      id: '3',
      name: 'Advanced Components.mp4',
      type: 'video',
      size: '189 MB',
      uploadDate: '2024-01-13',
      status: 'uploading',
      progress: 67,
      courseId: '2',
      courseName: 'Advanced JavaScript Patterns'
    }
  ]);

  // Mock courses data
  const courses = [
    { id: '1', title: 'Complete React Development' },
    { id: '2', title: 'Advanced JavaScript Patterns' },
    { id: '3', title: 'Modern CSS & Tailwind' },
    { id: '4', title: 'Node.js Backend Development' }
  ];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileInput = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const handleFiles = (files) => {
    if (!selectedCourse) {
      alert('Please select a course before uploading files');
      return;
    }

    const selectedCourseData = courses.find(c => c.id === selectedCourse);

    files.forEach((file) => {
      const newFile = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: file.name,
        type: getFileType(file.type),
        size: formatFileSize(file.size),
        uploadDate: new Date().toISOString().split('T')[0],
        status: 'uploading',
        progress: 0,
        courseId: selectedCourse,
        courseName: selectedCourseData?.title
      };

      setUploadedFiles(prev => [newFile, ...prev]);

      // Simulate upload progress
      simulateUpload(newFile.id);
    });
  };

  const getFileType = (mimeType) => {
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const simulateUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress >= 100) {
        progress = 100;
        setUploadedFiles(prev => 
          prev.map(file => 
            file.id === fileId 
              ? { ...file, status: 'completed', progress: 100 }
              : file
          )
        );
        clearInterval(interval);
      } else {
        setUploadedFiles(prev => 
          prev.map(file => 
            file.id === fileId 
              ? { ...file, progress }
              : file
          )
        );
      }
    }, 500);
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'video': return 'ri-video-line';
      case 'image': return 'ri-image-line';
      case 'audio': return 'ri-music-line';
      default: return 'ri-file-text-line';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'uploading': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1">
        <section className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-4 mb-4">
              <Link 
                to="/instructor" 
                className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg hover:bg-white/30 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </Link>
              <div>
                <h1 className="text-4xl font-bold">Upload Content</h1>
                <p className="text-xl text-purple-100 mt-2">
                  Upload videos, documents, and other course materials
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="border-b border-gray-200 mb-8">
              <div className="flex gap-8">
                {['upload', 'library', 'settings'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-4 px-1 font-medium capitalize whitespace-nowrap cursor-pointer ${
                      activeTab === tab
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'upload' && (
              <div className="space-y-8">
                <div className="bg-white border border-gray-200 rounded-xl p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Upload New Content</h2>
                  
                  {/* Course Selection */}
                  <div className="mb-8 bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      <i className="ri-book-line mr-2"></i>
                      Select Course to Upload Content *
                    </label>
                    <select
                      value={selectedCourse}
                      onChange={(e) => setSelectedCourse(e.target.value)}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Choose a course...</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.title}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-600 mt-2">
                      <i className="ri-information-line mr-1"></i>
                      All uploaded files will be assigned to this course
                    </p>
                  </div>

                  <div
                    className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                      dragActive 
                        ? 'border-purple-400 bg-purple-50' 
                        : selectedCourse 
                        ? 'border-gray-300 hover:border-gray-400'
                        : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                    onDragEnter={selectedCourse ? handleDrag : undefined}
                    onDragLeave={selectedCourse ? handleDrag : undefined}
                    onDragOver={selectedCourse ? handleDrag : undefined}
                    onDrop={selectedCourse ? handleDrop : undefined}
                  >
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-upload-cloud-line text-3xl text-purple-600"></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Drag and drop your files here
                    </h3>
                    <p className="text-gray-600 mb-6">
                      or click to browse and select files from your computer
                    </p>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileInput}
                      className="hidden"
                      id="file-upload"
                      accept="video/*,audio/*,image/*,.pdf,.doc,.docx,.ppt,.pptx"
                      disabled={!selectedCourse}
                    />
                    <label
                      htmlFor="file-upload"
                      className={`inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-lg transition-colors whitespace-nowrap ${
                        selectedCourse
                          ? 'bg-purple-600 text-white hover:bg-purple-700 cursor-pointer'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <i className="ri-folder-open-line text-xl"></i>
                      Browse Files
                    </label>
                    {!selectedCourse && (
                      <p className="text-sm text-red-600 mt-4">
                        <i className="ri-error-warning-line mr-1"></i>
                        Please select a course first
                      </p>
                    )}
                    <div className="mt-4 text-sm text-gray-500">
                      Supported formats: MP4, MOV, PDF, DOC, PPT, JPG, PNG (Max 500MB per file)
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                        <i className="ri-video-line text-2xl text-blue-600"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Video Content</h4>
                      <p className="text-sm text-gray-600">
                        Upload lecture videos, tutorials, and demonstrations
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                        <i className="ri-file-text-line text-2xl text-green-600"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Documents</h4>
                      <p className="text-sm text-gray-600">
                        Share PDFs, presentations, and course materials
                      </p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-3">
                        <i className="ri-image-line text-2xl text-yellow-600"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Images & Audio</h4>
                      <p className="text-sm text-gray-600">
                        Add visual aids, diagrams, and audio recordings
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Upload Tips</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                        <div>
                          <div className="font-medium text-gray-900">High Quality Videos</div>
                          <div className="text-sm text-gray-600">Use 1080p resolution for best viewing experience</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                        <div>
                          <div className="font-medium text-gray-900">Clear Audio</div>
                          <div className="text-sm text-gray-600">Ensure audio is clear and free from background noise</div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                        <div>
                          <div className="font-medium text-gray-900">Organized Content</div>
                          <div className="text-sm text-gray-600">Use descriptive file names for easy organization</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                        <div>
                          <div className="font-medium text-gray-900">Proper Formats</div>
                          <div className="text-sm text-gray-600">Stick to supported file formats for compatibility</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'library' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Content Library</h2>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search files..."
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                      <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                    <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8">
                      <option>All Courses</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.title}
                        </option>
                      ))}
                    </select>
                    <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8">
                      <option>All Types</option>
                      <option>Videos</option>
                      <option>Documents</option>
                      <option>Images</option>
                      <option>Audio</option>
                    </select>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">File</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Course</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Type</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Size</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Upload Date</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {uploadedFiles.map((file) => (
                          <tr key={file.id} className="hover:bg-gray-50">
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <i className={`${getFileIcon(file.type)} text-xl text-gray-600`}></i>
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{file.name}</div>
                                  {file.status === 'uploading' && file.progress && (
                                    <div className="mt-1">
                                      <div className="w-32 h-1 bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                          className="h-full bg-blue-500 transition-all duration-300"
                                          style={{ width: `${file.progress}%` }}
                                        ></div>
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">{Math.round(file.progress)}% uploaded</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <span className="text-sm text-gray-900">{file.courseName || 'Unassigned'}</span>
                            </td>
                            <td className="py-4 px-6">
                              <span className="capitalize text-gray-600">{file.type}</span>
                            </td>
                            <td className="py-4 px-6 text-gray-600">{file.size}</td>
                            <td className="py-4 px-6 text-gray-600">{file.uploadDate}</td>
                            <td className="py-4 px-6">
                              <span className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(file.status)}`}>
                                {file.status}
                              </span>
                            </td>
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-2">
                                <button className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                                  <i className="ri-eye-line text-gray-600"></i>
                                </button>
                                <button className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                                  <i className="ri-download-line text-gray-600"></i>
                                </button>
                                <button className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                                  <i className="ri-delete-bin-line text-red-600"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-8">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Upload Settings</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Default Video Quality
                      </label>
                      <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-8">
                        <option>1080p (Recommended)</option>
                        <option>720p</option>
                        <option>480p</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Auto-generate Subtitles
                      </label>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="auto-subtitles" className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" defaultChecked />
                        <label htmlFor="auto-subtitles" className="text-sm text-gray-600">
                          Automatically generate subtitles for uploaded videos
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        File Organization
                      </label>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <input type="radio" id="organize-course" name="organization" className="text-purple-600 focus:ring-purple-500" defaultChecked />
                          <label htmlFor="organize-course" className="text-sm text-gray-600">
                            Organize by course
                          </label>
                        </div>
                        <div className="flex items-center gap-3">
                          <input type="radio" id="organize-date" name="organization" className="text-purple-600 focus:ring-purple-500" />
                          <label htmlFor="organize-date" className="text-sm text-gray-600">
                            Organize by upload date
                          </label>
                        </div>
                        <div className="flex items-center gap-3">
                          <input type="radio" id="organize-type" name="organization" className="text-purple-600 focus:ring-purple-500" />
                          <label htmlFor="organize-type" className="text-sm text-gray-600">
                            Organize by file type
                          </label>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Storage Limit
                      </label>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Used Storage</span>
                          <span className="text-sm font-medium text-gray-900">2.4 GB / 10 GB</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-purple-500" style={{ width: '24%' }}></div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          76% storage remaining
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      <button className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer">
                        Save Settings
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Storage Management</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-video-line text-2xl text-blue-600"></i>
                      </div>
                      <div className="text-2xl font-bold text-gray-900">1.8 GB</div>
                      <div className="text-sm text-gray-600">Videos</div>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-file-text-line text-2xl text-green-600"></i>
                      </div>
                      <div className="text-2xl font-bold text-gray-900">456 MB</div>
                      <div className="text-sm text-gray-600">Documents</div>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-image-line text-2xl text-yellow-600"></i>
                      </div>
                      <div className="text-2xl font-bold text-gray-900">123 MB</div>
                      <div className="text-sm text-gray-600">Images</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}